import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { trackCheckoutCompleted, trackPricingViewed } from '../analytics';
import SubscriptionPlans from '../components/SubscriptionPlans';
import { Button } from '../components/ui/Button';
import {
  confirmCheckoutSession,
  createBillingPortalSession,
  cancelSubscription,
  fetchBillingConfig,
  reactivateSubscription,
  CHECKOUT_SESSION_STORAGE_KEY,
  type PaymentProvider,
} from '../api/billing';
import { upgradePlan as apiUpgradePlan } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { SubscriptionPlan } from '../data/subscriptions';
import { usePlanLabel } from '../i18n/usePlanLabel';
import { appTheme, pitchTheme } from '../styles/theme';

const Page = styled.main`
  flex: 1;
  padding: 48px 24px 64px;
  background: ${appTheme.pageBg};
`;

const Container = styled.div`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${appTheme.greenDark};
`;

const Intro = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.65;
  color: ${appTheme.textMuted};
  max-width: 52ch;
`;

const CurrentPlanBanner = styled.div`
  padding: 16px 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.mint};
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.text};
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
  width: 100%;

  strong {
    color: ${appTheme.greenDark};
  }
`;

const SuccessMessage = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.successBg};
  border: 1px solid #c5e8d4;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.successText};
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const CancelPanel = styled.section`
  padding: 24px 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CancelHeading = styled.h2`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.0625rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const CancelText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
`;

const CancelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ConfirmBox = styled.div`
  padding: 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const formatSubscriptionEndDate = (
  isoDate: string | null | undefined,
  fallback: string,
): string => {
  if (!isoDate) {
    return fallback;
  }
  return new Date(isoDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const SubscriptionPage: React.FC = () => {
  const { user, subscribeToPlan, refreshUser, applyUser } = useAuth();
  const { t } = useTranslation();
  const currentPlanLabel = usePlanLabel(user?.plan);
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradingPlan, setUpgradingPlan] = useState<SubscriptionPlan | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState<boolean | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackPricingViewed();
  }, []);

  useEffect(() => {
    fetchBillingConfig()
      .then((config) => {
        setBillingEnabled(config.enabled);
        setPaymentProvider(config.provider);
      })
      .catch(() => {
        setBillingEnabled(false);
        setPaymentProvider(null);
      });
  }, []);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId =
      searchParams.get('session_id') ?? sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);

    if (checkout === 'cancel') {
      setError(t('subscription.checkoutCanceled'));
      setSearchParams({}, { replace: true });
      return;
    }

    if (checkout !== 'success') {
      return;
    }

    const finalize = async () => {
      try {
        let completedPlan = user?.plan ?? 'basic';
        if (sessionId) {
          sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
          const confirmedUser = await confirmCheckoutSession(sessionId);
          applyUser(confirmedUser);
          completedPlan = confirmedUser.plan;
        } else {
          await refreshUser();
        }
        trackCheckoutCompleted(completedPlan);
        setSuccessMessage(t('subscription.checkoutSuccess'));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('subscription.confirmError'));
      } finally {
        setSearchParams({}, { replace: true });
      }
    };

    void finalize();
  }, [searchParams, setSearchParams, refreshUser, applyUser]);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (plan === 'free') {
      return;
    }

    setUpgradingPlan(plan);
    setError(null);
    setSuccessMessage(null);
    try {
      if (billingEnabled) {
        const result = await subscribeToPlan(plan);
        if (result === 'completed') {
          setSuccessMessage(t('subscription.upgraded', { plan: t(`plans.${plan}`) }));
        }
      } else {
        await apiUpgradePlan(plan);
        await refreshUser();
        setSuccessMessage(t('subscription.upgraded', { plan: t(`plans.${plan}`) }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription.checkoutError'));
    } finally {
      setUpgradingPlan(null);
    }
  };

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    setError(null);
    try {
      const { url } = await createBillingPortalSession();
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription.portalError'));
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updatedUser = await cancelSubscription();
      applyUser(updatedUser);
      setShowCancelConfirm(false);
      if (billingEnabled) {
        setSuccessMessage(
          t('subscription.canceledAtPeriodEnd', {
            date: formatSubscriptionEndDate(
              updatedUser.subscriptionEndsAt,
              t('subscription.billingPeriodEnd'),
            ),
          }),
        );
      } else {
        setSuccessMessage(t('subscription.canceledImmediate'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription.cancelError'));
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    setError(null);
    setSuccessMessage(null);
    try {
      applyUser(await reactivateSubscription());
      setSuccessMessage(t('subscription.reactivated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription.reactivateError'));
    } finally {
      setReactivating(false);
    }
  };

  const hasPaidPlan = user?.plan === 'basic' || user?.plan === 'premium';

  return (
    <Page>
      <Container>
        <Header>
          <TitleRow>
            <Title>{t('subscription.title')}</Title>
            {user?.hasBilling && billingEnabled && (
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                disabled={openingPortal}
                onClick={handleManageBilling}
              >
                {openingPortal ? t('subscription.opening') : t('subscription.manageBilling')}
              </Button>
            )}
          </TitleRow>
          <Intro>{t('subscription.intro')}</Intro>
        </Header>

        <CurrentPlanBanner>
          {user ? (
            <>
              <Trans
                i18nKey="subscription.currentPlan"
                values={{ plan: currentPlanLabel }}
                components={{ strong: <strong /> }}
              />
              {user.cancelAtPeriodEnd &&
                t('subscription.subscriptionEnds', {
                  date: formatSubscriptionEndDate(
                    user.subscriptionEndsAt,
                    t('subscription.billingPeriodEnd'),
                  ),
                })}
              {!user.cancelAtPeriodEnd && user.plan === 'free' && t('subscription.freeUpgradeHint')}
              {!user.cancelAtPeriodEnd && user.plan === 'basic' && t('subscription.basicUpgradeHint')}
              {!user.cancelAtPeriodEnd && user.plan === 'premium' && t('subscription.premiumFullAccess')}
            </>
          ) : (
            <>{t('subscription.comparePlans')}</>
          )}
        </CurrentPlanBanner>

        {successMessage && <SuccessMessage role="status">{successMessage}</SuccessMessage>}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <SubscriptionPlans
          currentPlan={user?.plan}
          showUpgradeActions
          billingEnabled={billingEnabled}
          paymentProvider={paymentProvider}
          upgradingPlan={upgradingPlan}
          onUpgrade={handleSubscribe}
        />

        {hasPaidPlan && (
          <CancelPanel>
            <CancelHeading>{t('subscription.cancelTitle')}</CancelHeading>
            {user?.cancelAtPeriodEnd ? (
              <>
                <CancelText>
                  {t('subscription.cancelActiveUntil', {
                    plan: currentPlanLabel,
                    date: formatSubscriptionEndDate(
                      user.subscriptionEndsAt,
                      t('subscription.billingPeriodEnd'),
                    ),
                  })}
                </CancelText>
                <CancelActions>
                  <Button
                    type="button"
                    $variant="primary"
                    disabled={reactivating || !billingEnabled}
                    onClick={handleReactivateSubscription}
                  >
                    {reactivating ? t('subscription.reactivating') : t('subscription.keepSubscription')}
                  </Button>
                </CancelActions>
              </>
            ) : showCancelConfirm ? (
              <ConfirmBox>
                <CancelText>
                  {billingEnabled
                    ? t('subscription.cancelConfirmBilling', { plan: currentPlanLabel })
                    : t('subscription.cancelConfirmImmediate', { plan: currentPlanLabel })}
                </CancelText>
                <CancelActions>
                  <Button
                    type="button"
                    $variant="danger"
                    disabled={canceling}
                    onClick={handleCancelSubscription}
                  >
                    {canceling ? t('subscription.canceling') : t('subscription.confirmCancellation')}
                  </Button>
                  <Button
                    type="button"
                    $variant="secondary"
                    disabled={canceling}
                    onClick={() => setShowCancelConfirm(false)}
                  >
                    {t('subscription.keepSubscription')}
                  </Button>
                </CancelActions>
              </ConfirmBox>
            ) : (
              <>
                <CancelText>
                  {billingEnabled
                    ? t('subscription.cancelAnytime')
                    : t('subscription.cancelImmediateHint')}
                </CancelText>
                <CancelActions>
                  <Button type="button" $variant="danger" onClick={() => setShowCancelConfirm(true)}>
                    {t('subscription.cancelSubscription')}
                  </Button>
                </CancelActions>
              </>
            )}
          </CancelPanel>
        )}
      </Container>
    </Page>
  );
};

export default SubscriptionPage;
